import { CartItems } from '@/hooks/CartContext';
import type {
    ComboCartItemAllocation,
    ComboCartItemsMap,
    ComboRequirementGroup,
} from './comboCartUtils';

const getCartItemPackagingQty = (item: Pick<CartItems, 'option_uid'>) => {
    const [, qtd] = item.option_uid?.split('_') || [];
    return Number(qtd);
};

const getCartItemPackagingCount = (item: CartItems) => item.quantity;

const isCartItemMatchingEligibleSku = (
    item: Pick<CartItems, 'sku' | 'option_uid'>,
    sku: { cod_produto: number; qtd_embalagem: number }
) => {
    if (Number(item.sku) !== Number(sku.cod_produto)) return false;
    const [, qtd] = item.option_uid?.split('_') || [];
    return Number(qtd) === Number(sku.qtd_embalagem);
};

const getGroupId = (grupo: ComboRequirementGroup): string => {
    const raw = grupo as unknown as Record<string, unknown>;
    const candidates = [raw.id, raw.id_grupo, raw.desc_grupo, raw.Desc_grupo];

    for (const value of candidates) {
        if (value != null && String(value).trim()) {
            return String(value).trim();
        }
    }

    return '';
};

const allocationBelongsToGrupo = (
    entry: ComboCartItemAllocation,
    grupo: ComboRequirementGroup
): boolean =>
    (grupo.skus_elegiveis || []).some(
        (sku) =>
            Number(entry.cod_produto) === Number(sku.cod_produto) &&
            Number(entry.qtd_embalagem) === Number(sku.qtd_embalagem)
    );

const getComboBenefitRules = (combo: { regras_beneficio?: unknown }) => {
    const regras = combo?.regras_beneficio;
    if (Array.isArray(regras)) return regras;
    return [];
};

const getComboMaxKitLimit = (combo: { regras_beneficio?: unknown }): number | null => {
    const regra = getComboBenefitRules(combo)[0] as { max_combo?: number | null } | undefined;
    if (regra?.max_combo == null) return null;
    const limit = Number(regra.max_combo);
    return Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : null;
};

const normalizeComboName = (combo: { id_campanha?: string; nome_campanha?: string }) => {
    if (combo.nome_campanha) return combo.nome_campanha;
    return combo?.id_campanha?.split('__').join(' + ')?.split('_').join(' ') || combo.id_campanha || '';
};

/** Identidade estável de uma instância (kit) de combo no carrinho. */
export const buildComboInstanceId = (idCampanha: string, kitIndex: number): string =>
    `${idCampanha}__KIT_${kitIndex}`;

/** Identidade estável de uma alocação (cartItem + combo + instância + grupo + SKU). */
export const buildComboAllocationId = (params: {
    cartItemId: string;
    comboId: string;
    comboInstanceId: string;
    groupId: string;
    cod_produto: number;
    qtd_embalagem: number;
}): string =>
    [
        params.cartItemId,
        params.comboId,
        params.comboInstanceId,
        params.groupId,
        params.cod_produto,
        params.qtd_embalagem,
    ].join('__');

export const getOtherInstancesAllocatedQuantity = (
    allocations: ComboCartItemAllocation[],
    comboInstanceId: string
): number =>
    allocations
        .filter(
            (entry) =>
                entry.allocatedPackaging > 0 &&
                entry.comboInstanceId &&
                entry.comboInstanceId !== comboInstanceId
        )
        .reduce((sum, entry) => sum + entry.allocatedPackaging, 0);

/** Ex.: "1 Grupo Completo de 3 Grupos" */
export const formatComboInstanceGroupsProgressLabel = (
    completeCount: number,
    totalCount: number
): string => {
    if (totalCount <= 0) return '';

    const completePart =
        completeCount === 1
            ? '1 Grupo Completo'
            : `${completeCount} Grupos Completos`;
    const totalPart = totalCount === 1 ? '1 Grupo' : `${totalCount} Grupos`;

    return `${completePart} de ${totalPart}`;
};

export const parseComboInstanceId = (
    comboInstanceId: string
): { idCampanha: string; kitIndex: number } | null => {
    const separator = '__KIT_';
    const index = comboInstanceId.lastIndexOf(separator);
    if (index <= 0) return null;

    const idCampanha = comboInstanceId.slice(0, index);
    const kitIndex = Number(comboInstanceId.slice(index + separator.length));

    if (!idCampanha || !Number.isFinite(kitIndex) || kitIndex <= 0) return null;

    return { idCampanha, kitIndex };
};

export interface ComboCartItemRelation {
    comboId: string;
    comboInstanceId: string;
    kitIndex: number;
    groupId: string;
    productId: number;
    cartItemId: string;
    quantity: number;
    appliesDiscount: boolean;
}

export interface ComboInstanceGroupStatus {
    grupo: ComboRequirementGroup;
    grupoId: string;
    required: number;
    currentQuantity: number;
    remaining: number;
    isComplete: boolean;
}

export interface ComboInstanceStatus {
    comboInstanceId: string;
    kitIndex: number;
    idCampanha: string;
    nomeCampanha: string;
    groups: ComboInstanceGroupStatus[];
    isComplete: boolean;
    appliesDiscount: boolean;
}

interface ComboInstanceSlot {
    comboInstanceId: string;
    kitIndex: number;
    groupRemaining: Map<string, number>;
}

const getCartItemsByInsertionOrder = (cartItems: CartItems[]) =>
    [...cartItems].sort((a, b) => {
        const da = a.addedAt ? new Date(a.addedAt).getTime() : 0;
        const db = b.addedAt ? new Date(b.addedAt).getTime() : 0;
        if (da !== db) return da - db;
        return String(a.uid).localeCompare(String(b.uid));
    });

export const findComboRequirementGroupForCartItem = (
    combo: { grupos_exigencia?: ComboRequirementGroup[] },
    item: Pick<CartItems, 'sku' | 'option_uid'>
): ComboRequirementGroup | undefined => {
    return (combo.grupos_exigencia || []).find((grupo) =>
        (grupo.skus_elegiveis || []).some((sku) => isCartItemMatchingEligibleSku(item, sku))
    );
};

const createInstanceSlot = (
    idCampanha: string,
    kitIndex: number,
    groups: ComboRequirementGroup[]
): ComboInstanceSlot => {
    const groupRemaining = new Map<string, number>();

    groups.forEach((grupo) => {
        groupRemaining.set(getGroupId(grupo), grupo.quantidade_base_por_kit ?? 0);
    });

    return {
        comboInstanceId: buildComboInstanceId(idCampanha, kitIndex),
        kitIndex,
        groupRemaining,
    };
};

const isInstanceSlotComplete = (slot: ComboInstanceSlot, groups: ComboRequirementGroup[]): boolean =>
    groups.every((grupo) => {
        const required = grupo.quantidade_base_por_kit ?? 0;
        // Grupo sem exigência numérica válida nunca conta como satisfeito — senão o kit fecha (e
        // libera desconto) ignorando esse grupo por completo. Mesma regra de getComboInstanceStatuses.
        if (required <= 0) return false;
        return (slot.groupRemaining.get(getGroupId(grupo)) ?? 0) <= 0;
    });

const findOpenSlotForGrupo = (
    slots: ComboInstanceSlot[],
    grupoId: string,
    groups: ComboRequirementGroup[]
): ComboInstanceSlot | undefined =>
    slots.find(
        (slot) => !isInstanceSlotComplete(slot, groups) && (slot.groupRemaining.get(grupoId) ?? 0) > 0
    );

const addAllocation = (
    map: ComboCartItemsMap,
    uid: string,
    allocation: ComboCartItemAllocation
) => {
    if (!map[uid]) {
        map[uid] = [allocation];
        return;
    }

    const existing = map[uid].find(
        (entry) =>
            entry.id_campanha === allocation.id_campanha &&
            entry.comboInstanceId === allocation.comboInstanceId &&
            entry.grupoId === allocation.grupoId &&
            entry.cod_produto === allocation.cod_produto &&
            entry.qtd_embalagem === allocation.qtd_embalagem &&
            entry.appliesDiscount === allocation.appliesDiscount
    );

    if (existing) {
        existing.allocatedPackaging += allocation.allocatedPackaging;
        return;
    }

    map[uid].push(allocation);
};

/**
 * Aloca embalagens do carrinho em instâncias independentes (kits) do combo.
 *
 * Regras:
 * - Cada instância tem suas próprias cotas por grupo.
 * - Produtos de instâncias concluídas não entram em instâncias novas.
 * - Excedente de quantidade abre nova instância automaticamente.
 * - appliesDiscount só para instâncias completas dentro de max_combo.
 */
export const buildComboInstanceAllocationsForCombo = (
    combo: {
        id_campanha: string;
        grupos_exigencia?: ComboRequirementGroup[];
        regras_beneficio?: unknown;
    },
    orderedCartItems: CartItems[]
): ComboCartItemsMap => {
    const map: ComboCartItemsMap = {};
    const groups = combo.grupos_exigencia || [];
    if (!groups.length) return map;

    const nomeCampanha = normalizeComboName(combo);
    const maxDiscountKits = getComboMaxKitLimit(combo) ?? Number.POSITIVE_INFINITY;
    const slots: ComboInstanceSlot[] = [];
    let nextKitIndex = 1;

    const unitEvents: Array<{ uid: string; item: CartItems }> = [];

    orderedCartItems.forEach((item) => {
        const packagingCount = getCartItemPackagingCount(item);
        for (let index = 0; index < packagingCount; index += 1) {
            unitEvents.push({ uid: item.uid, item });
        }
    });

    unitEvents.forEach(({ uid, item }) => {
        const grupo = findComboRequirementGroupForCartItem(combo, item);
        if (!grupo) return;

        const grupoId = getGroupId(grupo);

        let slot = findOpenSlotForGrupo(slots, grupoId, groups);

        if (!slot) {
            slot = createInstanceSlot(combo.id_campanha, nextKitIndex, groups);
            nextKitIndex += 1;
            slots.push(slot);
        }

        const remainingInGrupo = slot.groupRemaining.get(grupoId) ?? 0;
        if (remainingInGrupo <= 0) return;

        slot.groupRemaining.set(grupoId, remainingInGrupo - 1);

        addAllocation(map, uid, {
            id_campanha: combo.id_campanha,
            nome_campanha: nomeCampanha,
            comboInstanceId: slot.comboInstanceId,
            kitIndex: slot.kitIndex,
            grupoId,
            allocationId: buildComboAllocationId({
                cartItemId: uid,
                comboId: combo.id_campanha,
                comboInstanceId: slot.comboInstanceId,
                groupId: grupoId,
                cod_produto: Number(item.sku),
                qtd_embalagem: getCartItemPackagingQty(item),
            }),
            allocatedPackaging: 1,
            cod_produto: Number(item.sku),
            qtd_embalagem: getCartItemPackagingQty(item),
            appliesDiscount: false,
        });
    });

    let completeDiscountableKits = 0;

    slots.forEach((slot) => {
        const isComplete = isInstanceSlotComplete(slot, groups);
        const appliesDiscount = isComplete && completeDiscountableKits < maxDiscountKits;

        if (isComplete && appliesDiscount) {
            completeDiscountableKits += 1;
        }

        Object.values(map).forEach((lineAllocations) => {
            lineAllocations.forEach((entry) => {
                if (entry.comboInstanceId !== slot.comboInstanceId) return;
                entry.appliesDiscount = appliesDiscount;
            });
        });
    });

    return map;
};

export const mergeComboInstanceAllocationMaps = (
    maps: ComboCartItemsMap[]
): ComboCartItemsMap => {
    const merged: ComboCartItemsMap = {};

    maps.forEach((map) => {
        Object.entries(map).forEach(([uid, allocations]) => {
            allocations.forEach((allocation) => {
                addAllocation(merged, uid, { ...allocation });
            });
        });
    });

    return merged;
};

export const buildComboInstanceAllocationsMap = (
    cartItems: CartItems[],
    combos: Array<{
        id_campanha: string;
        grupos_exigencia?: ComboRequirementGroup[];
        regras_beneficio?: unknown;
    }>
): ComboCartItemsMap => {
    const orderedCartItems = getCartItemsByInsertionOrder(cartItems);

    const maps = combos.map((combo) =>
        buildComboInstanceAllocationsForCombo(combo, orderedCartItems)
    );

    return mergeComboInstanceAllocationMaps(maps);
};

export const allocationMatchesInstance = (
    entry: ComboCartItemAllocation,
    comboInstanceId: string
): boolean => {
    if (entry.comboInstanceId) {
        return entry.comboInstanceId === comboInstanceId;
    }

    // Compatibilidade: alocações legadas sem instância equivalem ao kit 1.
    const parsed = parseComboInstanceId(comboInstanceId);
    if (!parsed) return false;

    return parsed.kitIndex === 1 && entry.id_campanha === parsed.idCampanha;
};

export const getGroupAllocatedQuantityForInstance = (
    grupo: ComboRequirementGroup,
    idCampanha: string,
    comboInstanceId: string,
    cartItems: CartItems[],
    getAllocations: (uid: string) => ComboCartItemAllocation[] | undefined,
    mode: 'all' | 'discount' | 'in_progress' = 'all'
): number => {
    return cartItems.reduce((total, item) => {
        const allocs = getAllocations(item.uid) || [];
        const lineTotal = allocs
            .filter(
                (entry) =>
                    entry.id_campanha === idCampanha &&
                    allocationMatchesInstance(entry, comboInstanceId) &&
                    allocationBelongsToGrupo(entry, grupo) &&
                    entry.allocatedPackaging > 0
            )
            .filter((entry) => {
                if (mode === 'discount') return entry.appliesDiscount !== false;
                if (mode === 'in_progress') return entry.appliesDiscount === false;
                return true;
            })
            .reduce((sum, entry) => sum + entry.allocatedPackaging, 0);

        return total + lineTotal;
    }, 0);
};

export const getComboInstanceStatuses = (
    combo: {
        id_campanha: string;
        nome_campanha?: string;
        grupos_exigencia?: ComboRequirementGroup[];
        regras_beneficio?: unknown;
    },
    cartItems: CartItems[],
    getAllocations: (uid: string) => ComboCartItemAllocation[] | undefined
): ComboInstanceStatus[] => {
    const groups = combo.grupos_exigencia || [];
    const instanceIds = new Set<string>();

    cartItems.forEach((item) => {
        (getAllocations(item.uid) || []).forEach((entry) => {
            if (entry.id_campanha !== combo.id_campanha || !entry.comboInstanceId) return;
            instanceIds.add(entry.comboInstanceId);
        });
    });

    const maxDiscountKits = getComboMaxKitLimit(combo) ?? Number.POSITIVE_INFINITY;
    let discountableCompleteCount = 0;

    return Array.from(instanceIds)
        .map((comboInstanceId) => {
            const parsed = parseComboInstanceId(comboInstanceId);
            const kitIndex = parsed?.kitIndex ?? 1;

            const groupStatuses = groups.map((grupo) => {
                const grupoId = getGroupId(grupo);
                const required = grupo.quantidade_base_por_kit ?? 0;
                const currentQuantity = getGroupAllocatedQuantityForInstance(
                    grupo,
                    combo.id_campanha,
                    comboInstanceId,
                    cartItems,
                    getAllocations,
                    'all'
                );
                const remaining = Math.max(required - currentQuantity, 0);

                return {
                    grupo,
                    grupoId,
                    required,
                    currentQuantity,
                    remaining,
                    isComplete: required > 0 && remaining === 0,
                };
            });

            const isComplete = groupStatuses.every((status) => status.isComplete);
            const appliesDiscount = isComplete && discountableCompleteCount < maxDiscountKits;

            if (isComplete && appliesDiscount) {
                discountableCompleteCount += 1;
            }

            return {
                comboInstanceId,
                kitIndex,
                idCampanha: combo.id_campanha,
                nomeCampanha: combo.nome_campanha || combo.id_campanha,
                groups: groupStatuses,
                isComplete,
                appliesDiscount,
            };
        })
        .sort((a, b) => a.kitIndex - b.kitIndex);
};

export const isComboInstanceCompleted = (
    combo: { grupos_exigencia?: ComboRequirementGroup[] },
    comboInstanceId: string,
    cartItems: CartItems[],
    getAllocations: (uid: string) => ComboCartItemAllocation[] | undefined
): boolean => {
    const groups = combo.grupos_exigencia || [];
    const idCampanha = parseComboInstanceId(comboInstanceId)?.idCampanha;
    if (!idCampanha) return false;

    return groups.every((grupo) => {
        const required = grupo.quantidade_base_por_kit ?? 0;
        // Grupo sem exigência numérica válida nunca conta como satisfeito — mesma regra usada em
        // isInstanceSlotComplete/countCompleteKitsInCart/getComboInstanceStatuses.
        if (required <= 0) return false;

        const currentQuantity = getGroupAllocatedQuantityForInstance(
            grupo,
            idCampanha,
            comboInstanceId,
            cartItems,
            getAllocations,
            'all'
        );

        return currentQuantity >= required;
    });
};

export const getPrimaryComboInstanceForLine = (
    allocations: ComboCartItemAllocation[] | undefined,
    item?: Pick<CartItems, 'sku' | 'option_uid'>
): ComboCartItemAllocation | null => {
    if (!allocations?.length) return null;

    const relevant = allocations.filter((entry) => entry.allocatedPackaging > 0);

    if (item) {
        const qtdEmbalagem = getCartItemPackagingQty(item);
        const forProduct = relevant.filter(
            (entry) =>
                Number(entry.cod_produto) === Number(item.sku) &&
                Number(entry.qtd_embalagem) === qtdEmbalagem
        );

        if (forProduct.length) {
            return (
                forProduct.find((entry) => entry.appliesDiscount !== false) ||
                forProduct[forProduct.length - 1]
            );
        }
    }

    return relevant.find((entry) => entry.appliesDiscount !== false) || relevant[0] || null;
};

export const getComboInstanceIdsForLine = (
    allocations: ComboCartItemAllocation[] | undefined,
    idCampanha?: string,
    item?: Pick<CartItems, 'sku' | 'option_uid'>
): string[] => {
    if (!allocations?.length) return [];

    const qtdEmbalagem = item ? getCartItemPackagingQty(item) : null;
    const ids = new Set<string>();

    allocations.forEach((entry) => {
        if (entry.allocatedPackaging <= 0 || !entry.comboInstanceId) return;
        if (idCampanha && entry.id_campanha !== idCampanha) return;
        if (item) {
            if (Number(entry.cod_produto) !== Number(item.sku)) return;
            if (qtdEmbalagem != null && Number(entry.qtd_embalagem) !== qtdEmbalagem) return;
        }
        ids.add(entry.comboInstanceId);
    });

    return Array.from(ids).sort((a, b) => {
        const ka = parseComboInstanceId(a)?.kitIndex ?? 0;
        const kb = parseComboInstanceId(b)?.kitIndex ?? 0;
        return ka - kb;
    });
};
